import { z } from 'zod'

// Identity смотрящего — корень «среза». Источник — GET /auth/me: deal отдаёт его
// из claims сессии смотрящего (сотрудник — hub-session id.stit.tech, заказчик —
// customer-сессия хаба, 00-architecture § 3). role==='client' → заказчик, иначе
// (пусто | 'admin') → сотрудник stit.

export type SessionKind = 'employee' | 'customer'

export const MeSchema = z.object({
  sub: z.string(),
  username: z.string().optional(),
  name: z.string().optional(),
  // ФИО из customer-account заказчика — приоритетный источник display-имени.
  full_name: z.string().optional(),
  email: z.string().optional(),
  role: z.string().optional(),
  // Заказчик-контур: компания и account-роль гейтят /projects и /team.
  company_id: z.string().optional(),
  account_role: z.enum(['member', 'admin']).optional(),
  // Выданные заказчику продукты экосистемы (claim `products`) — по ним свитчер
  // рисует плитки. Словарь чужой (владелец — хаб), поэтому enum'ом не нарроим.
  products: z.array(z.string()).optional(),
})

export type Me = z.infer<typeof MeSchema>

export interface Session {
  kind: SessionKind
  id: string
  name: string
  email?: string
  companyId?: string
  // Сотрудник: hub-role из hub-session claim (00-architecture § 9.6). Заказчик: пусто.
  role?: string
  isAdmin: boolean // сотрудник hub-role=admin | заказчик account_role=admin — гейтит /team
  // Заказчик: продукты, выданные его аккаунту. Сотрудник: пусто — ему доступна
  // вся экосистема.
  products: string[]
}

export function sessionFromMe(me: Me): Session {
  const kind: SessionKind = me.role === 'client' ? 'customer' : 'employee'
  const name =
    me.full_name || me.name || me.username || (kind === 'customer' ? 'Заказчик' : 'Сотрудник')
  // Роль сотрудника приходит из хаба (hub-session claim); локальных admin-механик
  // (списков-исключений) больше нет. Заказчик — по account_role своего аккаунта.
  const isAdmin = kind === 'customer' ? me.account_role === 'admin' : me.role === 'admin'
  return {
    kind,
    id: me.sub,
    name,
    isAdmin,
    products: me.products ?? [],
    ...(kind === 'employee' && me.role ? { role: me.role } : {}),
    ...(me.email ? { email: me.email } : {}),
    ...(me.company_id ? { companyId: me.company_id } : {}),
  }
}

export const isEmployee = (session: Session | null): boolean => session?.kind === 'employee'
export const isCustomer = (session: Session | null): boolean => session?.kind === 'customer'
